import { useState } from 'react'
import { Group, Text, Badge, Anchor, Collapse, Stack } from '@mantine/core'
import { useFormat } from '../../_units'
import { useI18n } from '../../_i18n'
import {
  warningTypeLabel,
  localDateKey,
  warningPhase,
  warningStartMs,
  warningSeverityRank,
  WARNING_SEVERITY_COLOR,
} from './weatherData'

// Severity colour and ordering live in weatherData, shared with the per-day
// badges on the Weather band so the two cannot drift.

// "Sun, Jul 26, 8:35 – 9:45 PM" when both ends land on the same station-local
// day, "Sun, Jul 26, 11:40 PM – Mon, Jul 27, 2:00 AM" when they don't. The
// date is only worth printing once, and warnings routinely cross midnight, so
// this cannot be a blanket "drop the second date".
// The joining punctuation is a catalog string: CJK writes a range as
// 17:35～15:00 and an open end as 17:35～, where "17:35 – 15:00" and "17:35+"
// are Latin conventions.
function windowLabel(t, fmt, item, timezone) {
  const { starts_at: start, ends_at: end } = item
  if (!start) return ''
  if (!end) return t('warning_window_open', { start: fmt.dateTime(start, timezone) })
  const sameDay = !!timezone && localDateKey(start, timezone) === localDateKey(end, timezone)
  return t('warning_window_range', {
    start: fmt.dateTime(start, timezone),
    end: sameDay ? fmt.time(end, timezone) : fmt.dateTime(end, timezone),
  })
}

function WarningItem({ item, timezone }) {
  const [open, setOpen] = useState(false)
  const fmt = useFormat()
  const { t } = useI18n()
  const color = WARNING_SEVERITY_COLOR[item.severity] || 'gray'

  return (
    <Stack gap={4}>
      <Group gap={8} wrap="wrap" align="center">
        <Badge color={color} size="md" autoContrast>
          {warningTypeLabel(t, item.type)}
        </Badge>
        {/* Full body size, not caption size: the window is when the warning
            applies, which is substance rather than metadata. Dimmed keeps the
            badge as the row's loudest element without shrinking the facts. */}
        {item.starts_at && <Text c="dimmed">{windowLabel(t, fmt, item, timezone)}</Text>}
        <Anchor component="button" type="button" onClick={() => setOpen((v) => !v)}>
          {open ? t('warning_hide_text') : t('warning_show_text')} {open ? '▴' : '▾'}
        </Anchor>
      </Group>
      {/*
        item.text is rendered VERBATIM below — no truncation, no paraphrase, no
        re-casing. This is a legal requirement (Canada) and our policy
        everywhere: a reworded marine warning is a liability. The structure
        above (type/severity/times) is ours to badge and filter; the words in
        `text` are not.
      */}
      <Collapse expanded={open}>
        <Text
          component="pre"
          size="sm"
          style={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'var(--mantine-font-family-monospace)',
            margin: 0,
          }}
        >
          {item.text}
        </Text>
      </Collapse>
    </Stack>
  )
}

// In force beats upcoming; within a phase, whichever starts soonest.
const preferred = (a, b) =>
  a.phase === b.phase ? warningStartMs(a) < warningStartMs(b) : a.phase === 'in_force'

// Every warning worth showing, tagged with its phase, one badge per type.
//
// Returned ONLY when warnings.status === 'active'. The other two states
// (none_active, unavailable) yield an empty list and therefore no head at all:
// absence of a feed must never look like an all-clear, and neither must a feed
// that happens to be quiet. Nothing on this page says "no warnings".
//
// Within that, warningPhase drops expired windows and tags the rest. NOTHING
// else is filtered: an advisory two days out badges here with its window, the
// way NOAA's own page shows it. Keeping it off the page meant a reader who came
// for the warning they had already seen elsewhere found less than they arrived
// with. What it does not do is claim to be happening — that is the phase tag's
// job, and the summary paragraph's horizon.
//
// The feed can carry several windows of the same warning type (e.g. a
// small-craft advisory renewed for tonight after today's expires), so each type
// still collapses to one instance — but to the EARLIEST remaining one rather
// than the latest. Picking the latest was only ever a proxy for "not the
// expired one", and now that expiry is filtered explicitly the earliest is
// strictly better: it is the window in force now, or the next one to arrive,
// instead of the furthest-out renewal.
export function warningItems(weather, nowMs = Date.now()) {
  const { status, data } = weather || {}
  if (status !== 'ready' || !data || data.warnings?.status !== 'active') return []
  const byType = new Map()
  for (const item of data.warnings.items || []) {
    const phase = warningPhase(item, nowMs)
    if (!phase) continue
    const entry = { ...item, phase }
    const prev = byType.get(item.type)
    if (!prev || preferred(entry, prev)) byType.set(item.type, entry)
  }
  return [...byType.values()].sort(
    (a, b) =>
      warningSeverityRank(a) - warningSeverityRank(b) || warningStartMs(a) - warningStartMs(b),
  )
}

// The rows themselves, with no container of their own: they are the head of
// the conditions card (ConditionsSummary), not a separate banner. They used to
// be a standalone Paper sitting above the summary, and the two read as
// unrelated components describing one situation.
export default function WarningsHead({ items, timezone }) {
  return (
    <Stack gap="sm">
      {items.map((item) => (
        <WarningItem key={item.type} item={item} timezone={timezone} />
      ))}
    </Stack>
  )
}
